import { useCallback, useEffect, useState } from "react";
import { createFileRoute, redirect, useRouteContext } from "@tanstack/react-router";
import { Bell, Flag, Loader2, Send } from "lucide-react";
import { toast } from "sonner";

import { counted } from "@/braining/format";

import {
  checkTelegram,
  loadTelegramInfo,
  setTelegram,
  type TelegramInfo,
} from "@/braining/review-notify";
import {
  linkMyTelegram,
  loadMyTelegram,
  unlinkMyTelegram,
  type LinkState,
} from "@/braining/telegram-link.functions";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

/**
 * Уведомления о проверке.
 *
 * Аня: «когда будет готово, у нас будет алерт в систему тебе и Вике». Статус
 * «на проверке» руководитель увидит, только когда сам зайдёт, — Телеграм зовёт
 * зайти.
 *
 * Отдельным пунктом настроек, а не карточкой рядом с ключом AI: это третье
 * независимое подключение, и рядом они путались бы, как путались ключ AI и
 * доступ к Drive.
 */
export const Route = createFileRoute("/notifications")({
  beforeLoad: ({ context }) => {
    // Бота настраивает админ, но привязывает свой Телеграм каждый, кто
    // согласовывает: экран открыт руководителю, карточки с секретами — нет.
    const role = context.user?.role;
    if (role !== "admin" && role !== "rop") throw redirect({ to: "/" });
  },
  component: NotificationsPage,
});

function NotificationsPage() {
  const { user } = useRouteContext({ from: "__root__" });
  const [info, setInfo] = useState<TelegramInfo | null>(null);
  const [token, setToken] = useState("");
  const [saving, setSaving] = useState(false);
  const [checking, setChecking] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; message: string; proxy?: boolean } | null>(
    null,
  );

  useEffect(() => {
    void loadTelegramInfo()
      .then(setInfo)
      .catch((e: unknown) => console.error("[telegram] не удалось прочитать настройки", e));
  }, []);

  async function save() {
    setSaving(true);
    setResult(null);
    try {
      setInfo(await setTelegram({ data: { botToken: token.trim() } }));
      setToken("");
      toast.success("Настройки сохранены");
    } catch (e) {
      console.error("[telegram] не удалось сохранить настройки", e);
      toast.error("Не удалось сохранить настройки");
    } finally {
      setSaving(false);
    }
  }

  async function check() {
    setChecking(true);
    try {
      setResult(await checkTelegram());
    } catch (e) {
      console.error("[telegram] проверка не прошла", e);
      setResult({ ok: false, message: "Сервер не ответил" });
    } finally {
      setChecking(false);
    }
  }

  return (
    <div className="max-w-3xl space-y-5">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Уведомления</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Куда сообщать, что калькулятор ждёт проверки и что кто-то пожаловался на ошибку. Правит
          администратор.
        </p>
      </div>

      <MyTelegramCard />

      {user?.role !== "admin" ? null : (
        <Card>
          <CardContent className="space-y-4 py-4">
            <div className="flex items-center gap-2">
              <Bell className="size-4 text-muted-foreground" />
              <span className="text-sm font-medium">Телеграм-бот</span>
              {info && !info.isSet && (
                <span className="text-xs text-warning-ink">
                  не настроен — никто ничего не получит
                </span>
              )}
            </div>

            <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
              <p>
                Бот заводится у <span className="font-mono text-xs">@BotFather</span> — он выдаёт
                токен. Дальше руководитель должен сам написать боту «/start»: пока он этого не
                сделал, бот не имеет права написать ему первым.
              </p>
              <p className="mt-2">
                Больше ничего вписывать не нужно. Уведомление о калькуляторе уходит в личку каждому
                руководителю, кто привязал Телеграм в карточке выше, — и чьё решение пришло первым,
                то и принимается. Так это переживает отпуск: звать некого только тогда, когда не
                привязался никто.
              </p>
            </div>

            {info?.isSet && (
              <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm">
                <p className="text-muted-foreground">
                  Бот задан, токен оканчивается на{" "}
                  <span className="font-mono text-xs">{info.tokenTail}</span>.{" "}
                  {info.linked === 0
                    ? "Телеграм не привязал никто — звать пока некого."
                    : `Привязали: ${counted(info.linked, "руководитель", "руководителя", "руководителей")}.`}
                </p>
              </div>
            )}

            <div className="space-y-1.5">
              <div className="space-y-1.5">
                <Label className="text-xs font-normal text-muted-foreground">Токен бота</Label>
                <Input
                  value={token}
                  placeholder={info?.isSet ? "оставьте пустым, чтобы не менять" : "123456:AA…"}
                  className="font-mono text-xs"
                  onChange={(e) => setToken(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant={info?.isSet ? "outline" : "default"}
                onClick={() => void save()}
                disabled={saving || token.trim().length < 20}
              >
                {saving && <Loader2 className="size-4 animate-spin" />}
                {info?.isSet ? "Заменить" : "Сохранить"}
              </Button>

              {/* Проверка связи — как у AI-провайдера: настройку с двумя
                секретами вслепую не отладить, а выяснять «дошло или нет» на
                живой смете поздно. */}
              <Button
                variant="ghost"
                onClick={() => void check()}
                disabled={checking || !info?.isSet}
              >
                {checking && <Loader2 className="size-4 animate-spin" />}
                Проверить связь
              </Button>
            </div>

            {result && (
              <p
                className={
                  result.ok
                    ? "text-sm text-success-ink"
                    : "rounded-md border border-destructive/40 bg-destructive/5 px-3 py-2 text-sm text-destructive-ink"
                }
              >
                {result.message}
                {/* Через прокси или напрямую — первое, что нужно знать, когда
                  Телеграм молчит: с российского адреса он не отвечает вовсе. */}
                {result.proxy !== undefined && (
                  <span className="ml-1 opacity-70">
                    {result.proxy ? "(через прокси)" : "(напрямую, без прокси)"}
                  </span>
                )}
              </p>
            )}

            {info && !info.baseUrl && (
              <p className="text-xs text-warning-ink">
                В окружении сервиса не задан PUBLIC_URL — уведомление уйдёт без ссылки на
                калькулятор.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Канала жалоб здесь нет намеренно. Он принадлежит тому, кто чинит
          продукт, а не агентству: менять его им нельзя, а знать о нём незачем —
          для них жалоба заканчивается экраном «Жалобы». Настроен он или нет,
          видно в журнале сервиса при старте (`server/plugins/telegram-bot.ts`). */}
    </div>
  );
}

/**
 * Мой Телеграм.
 *
 * Руководителю приходит калькулятор на согласование с кнопками «Согласовано»
 * и «Нужны правки», и нажатие меняет статус. Значит система обязана знать,
 * кто нажал: адрес общего чата для этого не годится — он говорит, куда слать,
 * а не кто отвечает.
 *
 * Привязывает человек сам: код виден только на его экране, живёт пятнадцать
 * минут и сгорает при первом использовании.
 */
function MyTelegramCard() {
  const [state, setState] = useState<LinkState | null>(null);
  const [code, setCode] = useState<{ code: string; bot: string | null } | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    loadMyTelegram()
      .then(setState)
      .catch((e: unknown) => console.error("[telegram] привязку не прочитали", e));
  }, []);

  useEffect(load, [load]);

  async function link() {
    setBusy(true);
    try {
      setCode(await linkMyTelegram());
    } catch (e) {
      console.error("[telegram] код не выдан", e);
      toast.error("Не удалось получить код");
    } finally {
      setBusy(false);
    }
  }

  async function drop() {
    setBusy(true);
    try {
      await unlinkMyTelegram();
      setCode(null);
      toast.success("Телеграм отвязан");
      load();
    } catch (e) {
      console.error("[telegram] отвязать не вышло", e);
      toast.error("Не удалось отвязать");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardContent className="space-y-4 py-4">
        <div className="flex items-center gap-2">
          <Send className="size-4 text-muted-foreground" />
          <span className="text-sm font-medium">Мой Телеграм</span>
          {state?.linked ? (
            <span className="text-xs text-success-ink">
              привязан{state.name ? ` · ${state.name}` : ""}
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">не привязан</span>
          )}
        </div>

        <div className="rounded-md border bg-muted/30 px-3 py-2.5 text-sm text-muted-foreground">
          <p>
            Калькуляторы на согласование приходят в личку с кнопками «Согласовано» и «Нужны правки».
            Нажатие меняет статус в системе, поэтому Телеграм должен знать, кто нажал, — для этого и
            привязка.
          </p>
          <p className="mt-2">
            Замечания пишутся ответом на сообщение бота: он спросит, что поправить, и передаст
            менеджеру дословно.
          </p>
        </div>

        {code && (
          <div className="space-y-2 rounded-md border border-primary/30 bg-primary/5 px-3 py-2.5 text-sm">
            <p>
              Код привязки: <span className="font-mono text-base">{code.code}</span>
            </p>
            {code.bot ? (
              <p>
                <a
                  href={`https://t.me/${code.bot}?start=${code.code}`}
                  target="_blank"
                  rel="noreferrer"
                  className="text-primary hover:underline"
                >
                  Открыть бота и привязать
                </a>{" "}
                — или отправьте ему «/start {code.code}» сами.
              </p>
            ) : (
              <p className="text-muted-foreground">
                Имя бота не удалось узнать — отправьте ему «/start {code.code}» вручную.
              </p>
            )}
            <p className="text-xs text-muted-foreground">
              Код действует пятнадцать минут. После привязки обновите страницу.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant={state?.linked ? "outline" : "default"}
            onClick={() => void link()}
            disabled={busy}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            {state?.linked ? "Привязать заново" : "Привязать"}
          </Button>
          {state?.linked && (
            <Button variant="ghost" onClick={() => void drop()} disabled={busy}>
              Отвязать
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
